import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { projectKeys, useInvalidateProjects } from './api'
import { mapCatalogModule, mapChangelog, mapFeature, mapProjectModule, mapSector, moduleBody, money, rowsOf, ZERO_PRICE } from './map'
import type { CatalogModuleInput, ModuleChangelogInput, ModuleFeatureInput, ModuleLinkInput, PricingType, ProductPackage, SectorInput } from './types'

async function loadModule(id: string) {
  const [mod, features, changelog, deps] = await Promise.all([
    api.get<Record<string, unknown>>(`/api/v1/catalog/modules/${id}`),
    api.get<unknown>(`/api/v1/catalog/modules/${id}/features`),
    api.get<unknown>(`/api/v1/catalog/modules/${id}/changelog`),
    api.get<unknown>(`/api/v1/catalog/modules/${id}/dependencies`),
  ])
  const depRow = (deps && typeof deps === 'object' ? deps : {}) as Record<string, unknown>
  const ids = Array.isArray(depRow.required_module_ids) ? depRow.required_module_ids.map(String) : rowsOf(deps).map((row) => String(row.required_module_id ?? row.module_id ?? row.id))
  return mapCatalogModule(mod, { features: rowsOf(features).map(mapFeature), changelog: rowsOf(changelog).map(mapChangelog), dependsOn: ids })
}

export const useCatalog = () => useQuery({ queryKey: projectKeys.catalog, queryFn: async () => rowsOf(await api.get<unknown>('/api/v1/catalog/modules')).map((row) => mapCatalogModule(row)) })
export const useSectors = () => useQuery({ queryKey: [...projectKeys.catalog, 'sectors'], queryFn: async () => rowsOf(await api.get<unknown>('/api/v1/catalog/sectors')).map(mapSector) })
export const useProductSectors = (productId: string) =>
  useQuery({
    queryKey: [...projectKeys.catalog, 'product-sectors', productId],
    enabled: !!productId,
    queryFn: async () =>
      rowsOf(await api.get<unknown>('/api/v1/catalog/product-sectors', { product_id: productId })).map((row) => ({
        id: String(row.id ?? ''),
        code: String(row.code ?? ''),
        name: String(row.name ?? ''),
        productId: String(row.product_id ?? ''),
      })),
  })

export function useSectorMutations() {
  const invalidate = useInvalidateProjects()
  const qc = useQueryClient()
  const onSuccess = () => Promise.all([invalidate(), qc.invalidateQueries({ queryKey: ['crm'] })])
  return {
    save: useMutation({
      mutationFn: ({ id, input }: { id?: string; input: SectorInput }) =>
        id
          ? api.put(`/api/v1/catalog/sectors/${id}`, { name: input.name, description: input.description, is_active: true })
          : api.post('/api/v1/catalog/sectors', { code: input.name.toLowerCase().replace(/\s+/g, '-').slice(0, 32), name: input.name, description: input.description }),
      onSuccess,
    }),
    remove: useMutation({ mutationFn: (id: string) => api.del(`/api/v1/catalog/sectors/${id}`), onSuccess }),
  }
}

export const useProductPackages = () =>
  useQuery({
    queryKey: [...projectKeys.catalog, 'packages'],
    queryFn: async () => {
      const [projects, catalog] = await Promise.all([api.get<unknown>('/api/v1/projects'), api.get<unknown>('/api/v1/catalog/modules')])
      const modules = rowsOf(catalog).map((row) => mapCatalogModule(row))
      const byId = new Map(modules.map((m) => [m.id, m]))
      const packs = await Promise.all(
        rowsOf(projects).map(async (project) => {
          const links = rowsOf(await api.get<unknown>(`/api/v1/projects/${String(project.id)}/modules`))
          const base: Record<PricingType, string> = { ...ZERO_PRICE }
          const addons: Record<PricingType, string> = { ...ZERO_PRICE }
          let requiredCount = 0
          let optionalCount = 0
          for (const link of links) {
            const view = mapProjectModule(link, byId.get(String(link.module_id ?? link.id)))
            const bucket = view.requirement === 'required' ? base : addons
            if (view.requirement === 'required') requiredCount += 1
            else optionalCount += 1
            const cents = Math.round(Number(bucket[view.pricing]) * 100) + Math.round(Number(view.price) * 100)
            bucket[view.pricing] = (cents / 100).toFixed(2)
          }
          return { projectId: String(project.id ?? ''), requiredCount, optionalCount, base, addons } satisfies ProductPackage
        }),
      )
      return packs
    },
  })

export function useSaveCatalogModule() {
  const invalidate = useInvalidateProjects()
  return useMutation({
    mutationFn: async ({ id, input }: { id?: string; input: CatalogModuleInput }) => {
      const saved = id
        ? await api.put<Record<string, unknown>>(`/api/v1/catalog/modules/${id}`, moduleBody(input))
        : await api.post<Record<string, unknown>>('/api/v1/catalog/modules', moduleBody({ ...input, code: input.code }))
      const moduleId = id || String(saved.id ?? '')
      if (moduleId) await api.put(`/api/v1/catalog/modules/${moduleId}/dependencies`, { required_module_ids: input.dependsOn })
      return saved
    },
    onSuccess: invalidate,
  })
}

export function useDeleteCatalogModule() {
  const invalidate = useInvalidateProjects()
  return useMutation({ mutationFn: (id: string) => api.del(`/api/v1/catalog/modules/${id}`), onSuccess: invalidate })
}

export function useModuleLinks() {
  const invalidate = useInvalidateProjects()
  return {
    integrate: useMutation({
      mutationFn: async ({ projectId, moduleId, input }: { projectId: string; moduleId: string; input: ModuleLinkInput }) => {
        await api.post(`/api/v1/projects/${projectId}/modules`, { module_id: moduleId, project_price: input.price, is_mandatory: input.requirement === 'required' })
        return [] as string[]
      },
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ projectId, moduleId, input }: { projectId: string; moduleId: string; input: ModuleLinkInput }) =>
        api.put('/api/v1/catalog/price-list', {
          project_id: projectId,
          items: [{ module_id: moduleId, project_price: input.price, is_mandatory: input.requirement === 'required', is_optional: input.requirement !== 'required', is_special: false }],
        }),
      onSuccess: invalidate,
    }),
    unlink: useMutation({
      mutationFn: ({ projectId, moduleId }: { projectId: string; moduleId: string }) => api.del(`/api/v1/projects/${projectId}/modules/${moduleId}`),
      onSuccess: invalidate,
    }),
  }
}

export function useDeleteFeature() {
  const invalidate = useInvalidateProjects()
  return useMutation({ mutationFn: ({ id }: { moduleId: string; id: string }) => api.del(`/api/v1/catalog/features/${id}`), onSuccess: invalidate })
}

export const useCatalogModule = (id: string) => useQuery({ queryKey: [...projectKeys.catalog, id], queryFn: () => loadModule(id), enabled: !!id })

export function useModuleContent(moduleId: string) {
  const invalidate = useInvalidateProjects()
  const m = <A,>(fn: (arg: A) => Promise<unknown>) => ({ mutationFn: fn, onSuccess: invalidate })
  return {
    saveFeature: useMutation(
      m(({ id, input }: { id?: string; input: ModuleFeatureInput }) => {
        const body = { title: input.name, group_name: input.group, sort_order: 0, description: input.description }
        return id ? api.put(`/api/v1/catalog/features/${id}`, body) : api.post(`/api/v1/catalog/modules/${moduleId}/features`, body)
      }),
    ),
    deleteFeature: useMutation(m((id: string) => api.del(`/api/v1/catalog/features/${id}`))),
    saveChangelog: useMutation(
      m(({ id, input }: { id?: string; input: ModuleChangelogInput }) => {
        const body = { version: input.version, release_date: input.date, notes: input.notes }
        return id ? api.put(`/api/v1/catalog/modules/${moduleId}/changelog/${id}`, body) : api.post(`/api/v1/catalog/modules/${moduleId}/changelog`, body)
      }),
    ),
    deleteChangelog: useMutation(m((id: string) => api.del(`/api/v1/catalog/modules/${moduleId}/changelog/${id}`))),
  }
}

export { money }
